import { useState } from 'react';

export interface ComplaintPayload {
  orderId: string;
  type: string;
  description: string;
  priority: string;
  attachments: File[];
  submittedAt: string;
}

interface ComplaintFormProps {
  orderId: string;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (complaint: ComplaintPayload) => void;
}

export default function ComplaintForm({ orderId, isOpen, onClose, onSubmit }: ComplaintFormProps) {
  const [complaintData, setComplaintData] = useState({
    type: '',
    description: '',
    priority: 'normal',
    attachments: [] as File[]
  });

  const [validationError, setValidationError] = useState<string | null>(null);

  const complaintTypes = [
    { id: 'retard', label: 'Retard de livraison', icon: 'ri-time-line' },
    { id: 'colis_endommage', label: 'Colis endommagé / Ouvert', icon: 'ri-error-warning-line' },
    { id: 'colis_perdu', label: 'Colis non reçu / Perdu', icon: 'ri-question-line' },
    { id: 'livreur_comportement', label: 'Incompréhension avec l\'agent', icon: 'ri-user-voice-line' },
    { id: 'adresse_incorrecte', label: 'Problème de localisation', icon: 'ri-map-pin-flash-line' },
    { id: 'autre', label: 'Autre motif', icon: 'ri-more-line' }
  ];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError(null);

    if (!complaintData.type) {
      setValidationError('Veuillez sélectionner le type de problème rencontré.');
      return;
    }
    if (!complaintData.description.trim() || complaintData.description.length < 10) {
      setValidationError('La description doit contenir au moins 10 caractères pour être traitée.');
      return;
    }

    onSubmit({
      orderId,
      ...complaintData,
      submittedAt: new Date().toISOString()
    });

    // Reset du state local
    setComplaintData({
      type: '',
      description: '',
      priority: 'normal',
      attachments: []
    });
    onClose();
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setComplaintData(prev => ({
      ...prev,
      attachments: [...prev.attachments, ...files].slice(0, 3) // Restriction stricte à 3 fichiers max
    }));
  };

  const removeFile = (index: number) => {
    setComplaintData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-slate-950/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4 z-50 animate-fade-in">

      {/* Container Principal adaptatif: plein écran bas sur mobile, modal centré sur desktop */}
      <div className="bg-white w-full sm:max-w-xl rounded-t-2xl sm:rounded-2xl max-h-[92vh] sm:max-h-[85vh] flex flex-col shadow-2xl border border-slate-100 overflow-hidden animate-slide-up">

        {/* Header du Modal */}
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50">
          <div>
            <div className="flex items-center space-x-2">
              <span className="w-2 h-2 rounded-full bg-rose-500 animate-pulse"></span>
              <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wide">
                Ouverture d'un litige
              </h3>
            </div>
            <p className="text-xs text-slate-400 mt-0.5 font-mono">ID Commande : #{orderId}</p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center bg-white border border-slate-200 hover:bg-slate-50 text-slate-500 hover:text-slate-800 rounded-xl transition-all"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        {/* Formulaire défilant verticalement de manière fluide */}
        <form onSubmit={handleSubmit} className="p-5 overflow-y-auto space-y-5 flex-1 no-scrollbar">

          {validationError && (
            <div className="p-3 bg-rose-50 border border-rose-100 text-rose-700 rounded-xl text-xs font-semibold flex items-center space-x-2">
              <i className="ri-error-warning-fill text-sm"></i>
              <span>{validationError}</span>
            </div>
          )}

          {/* 1. Catégorie du problème */}
          <div>
            <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2.5">
              Nature du problème <span className="text-rose-500">*</span>
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {complaintTypes.map((type) => {
                const isSelected = complaintData.type === type.id;
                return (
                  <button
                    key={type.id}
                    type="button"
                    onClick={() => {
                      setValidationError(null);
                      setComplaintData(prev => ({ ...prev, type: type.id }));
                    }}
                    className={`flex items-center space-x-3 p-3 border rounded-xl text-left transition-all ${isSelected
                        ? 'border-rose-500 bg-rose-50/50 text-rose-900 font-semibold'
                        : 'border-slate-100 bg-slate-50/50 text-slate-600 hover:bg-slate-50 hover:border-slate-200'
                      }`}
                  >
                    <i className={`${type.icon} text-base ${isSelected ? 'text-rose-600' : 'text-slate-400'}`}></i>
                    <span className="text-xs">{type.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* 2. Zone de texte descriptive */}
          <div>
            <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">
              Explication détaillée <span className="text-rose-500">*</span>
            </label>
            <textarea
              value={complaintData.description}
              onChange={(e) => setComplaintData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Veuillez décrire l'anomalie avec précision (horaires, lieu, contexte) pour faciliter les investigations de nos agents support..."
              className="w-full text-xs md:text-sm px-3 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:border-slate-900 transition-colors placeholder:text-slate-300 resize-none leading-relaxed"
              rows={4}
              maxLength={500}
            />
            <div className="text-[10px] text-slate-400 text-right mt-1 font-medium">
              {complaintData.description.length}/500 caractères
            </div>
          </div>

          {/* 3. Sélecteur de niveau d'urgence */}
          <div>
            <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">
              Niveau de criticité logistique
            </label>
            <div className="relative">
              <select
                value={complaintData.priority}
                onChange={(e) => setComplaintData(prev => ({ ...prev, priority: e.target.value }))}
                className="w-full text-xs bg-slate-50 border border-slate-200 text-slate-700 rounded-xl px-3 py-3 appearance-none focus:outline-none focus:border-slate-900 font-semibold cursor-pointer"
              >
                <option value="normal">Standard (Résolution sous 24h-48h)</option>
                <option value="urgent">Prioritaire (Impact sur livraison en cours)</option>
                <option value="critique">Critique / Urgent (Perte de valeur commerciale)</option>
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-slate-400">
                <i className="ri-arrow-down-s-line text-base"></i>
              </div>
            </div>
          </div>

          {/* 4. Zone d'upload multimédia (Preuves de livraison ou d'état du colis) */}
          <div>
            <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">
              Preuves visuelles ou bordereau (Optionnel)
            </label>
            <div className="border-2 border-dashed border-slate-200 hover:border-slate-400 rounded-xl p-4 bg-slate-50/50 transition-colors text-center relative group">
              <input
                type="file"
                multiple
                accept="image/*,.pdf"
                onChange={handleFileUpload}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                id="file-upload"
              />
              <div className="flex flex-col items-center justify-center">
                <div className="w-9 h-9 bg-white border border-slate-100 rounded-xl flex items-center justify-center text-slate-400 shadow-sm group-hover:scale-105 transition-transform mb-2">
                  <i className="ri-camera-lens-line text-lg"></i>
                </div>
                <p className="text-xs font-semibold text-slate-700">
                  Joindre une photo du colis ou document
                </p>
                <p className="text-[10px] text-slate-400 mt-0.5">
                  Fichiers acceptés : PNG, JPG, PDF &bull; Limite de 3 éléments
                </p>
              </div>
            </div>

            {/* Liste horizontale / verticale des captures téléversées */}
            {complaintData.attachments.length > 0 && (
              <div className="mt-3 gap-2 space-y-1.5">
                {complaintData.attachments.map((file, index) => (
                  <div key={index} className="flex items-center justify-between bg-slate-50 border border-slate-100 px-3 py-2 rounded-xl text-xs">
                    <div className="flex items-center space-x-2 min-w-0">
                      <i className="ri-file-shield-2-line text-slate-400 text-base flex-shrink-0"></i>
                      <span className="text-slate-700 truncate font-medium">{file.name}</span>
                      <span className="text-[10px] text-slate-400 flex-shrink-0">({(file.size / 1024).toFixed(0)} KB)</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeFile(index)}
                      className="w-6 h-6 flex items-center justify-center bg-white border border-slate-100 text-rose-500 hover:text-rose-700 rounded-lg shadow-sm transition-colors"
                    >
                      <i className="ri-delete-bin-6-line text-xs"></i>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Boutons d'actions ancrés au pied du formulaire */}
          <div className="flex space-x-3 pt-4 border-t border-slate-100">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-3 border border-slate-200 hover:bg-slate-50 text-slate-700 rounded-xl text-xs font-bold transition-all"
            >
              Retour
            </button>
            <button
              type="submit"
              className="flex-1 py-3 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-xs font-bold transition-all shadow-md shadow-slate-900/10"
            >
              Déposer le dossier
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}